import { useState } from 'react';
import { useTheme } from '@emotion/react';

import { Flex, Input } from '@toolbox/components';
import { useField } from '@toolbox/form';
import { isNullish, isNumberOrNull } from '@toolbox/utils';

import type { Modify } from '@toolbox/utils';
import type { FieldPath, FieldValues, PathValue, UnpackNestedValue } from '@toolbox/form';
import type { InputProps } from '@toolbox/components';
import type { ChangeEvent } from 'react';

export type NumberInputFieldProps<Name extends string> = Modify<
  InputProps,
  {
    name: Name;
    shouldUnregister?: boolean;
    positive?: boolean;
    showTooltip?: boolean;
    tooltip?: string;
    decimalPrecision?: number;
    helperButtonStep: number;
    useHelperButtons?: boolean;
    defaultValue?: number | null;
    defaultErrorMessage?: string;
  }
>;

export const NUMBER_REGEX = /^-?(([1-9][0-9]{0,15}|0)(\.\d*)?)?$/;

export const NumberInputField = <
  FD extends FieldValues = FieldValues,
  Name extends FieldPath<FD> = FieldPath<FD>,
>({
  name,
  keepErrorSpace = true,
  defaultValue,
  defaultErrorMessage = 'Ihre Eingabe ist nicht korrekt',
  required,
  positive,
  showTooltip,
  tooltip,
  decimalPrecision,
  helperButtonStep,
  useHelperButtons,
  min,
  max,
  ...props
}: NumberInputFieldProps<Name>): JSX.Element => {
  const { field, fieldState } = useField<FD, Name>({
    name,
    defaultValue: defaultValue as UnpackNestedValue<PathValue<FD, Name>>,
    rules: {
      required,
      min,
      max,
      validate: () => isNumberOrNull,
    },
  });

  const [value, setValue] = useState<string>(field.value ?? '');
  const theme = useTheme();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const cursor = event.target.selectionStart ?? event.target.value.length;
    const sanitizedValue = event.target.value.replace(',', '.');

    const isValid = isValidateValue(sanitizedValue, { positive, decimalPrecision });

    // when we force change the value it loses cursor position
    // so we're trying to restore it's previous position
    if (!isValid || sanitizedValue !== event.target.value) {
      const newCursor = !isValid && cursor > 0 ? cursor - 1 : cursor;

      setTimeout(() => {
        event.target.selectionStart = newCursor;
        event.target.selectionEnd = newCursor;
      }, 0);
    }

    if (!isValid) {
      return;
    }

    setValue(sanitizedValue);

    const trimmedValue = sanitizedValue.endsWith('.')
      ? sanitizedValue.slice(0, -1)
      : sanitizedValue;
    const value = trimmedValue ? Number(trimmedValue) : null;

    if (isNumberOrNull(value)) {
      field.onChange(value);
    }
  };

  const Decrement = (): void => {
    const sanitizedValue = value.toString().replace(',', '.');
    const trimmedValue = sanitizedValue.endsWith('.')
      ? sanitizedValue.slice(0, -1)
      : sanitizedValue;
    const valueNumber = trimmedValue ? Number(trimmedValue) : 0;

    if (valueNumber <= helperButtonStep) {
      setValue((0).toString());
      field.onChange(0);
      return;
    }

    if (isNumberOrNull(valueNumber)) {
      setValue((valueNumber - helperButtonStep).toString());
      field.onChange(valueNumber - helperButtonStep);
    }
  };

  const Increment = (): void => {
    const sanitizedValue = value.toString().replace(',', '.');
    const trimmedValue = sanitizedValue.endsWith('.')
      ? sanitizedValue.slice(0, -1)
      : sanitizedValue;
    const valueNumber = trimmedValue ? Number(trimmedValue) : 0;

    if (isNumberOrNull(valueNumber)) {
      setValue((valueNumber + helperButtonStep).toString());
      field.onChange(valueNumber + helperButtonStep);
    }
  };

  const input = (
    <Input
      {...props}
      {...field}
      inputMode="decimal"
      showTooltip={showTooltip}
      tooltip={tooltip}
      keepErrorSpace={keepErrorSpace}
      error={fieldState.error?.message || (fieldState.invalid && defaultErrorMessage)}
      value={value}
      onChange={handleChange}
    />
  );

  if (!useHelperButtons) {
    return input;
  }
  return (
    <Flex style={{ width: '100%', columnGap: '10px', justifyContent: 'center' }}>
      {useHelperButtons && (
        <Flex
          justifyContent={'center'}
          alignItems="center"
          onClick={Decrement}
          style={{
            height: '60px',
            width: '60px',
            fontSize: '25px',
            flexShrink: 0,
            backgroundColor: theme.palette.colors.petrol[400],
            color: 'white',
            cursor: 'pointer',
          }}
        >
          -
        </Flex>
      )}
      {input}
      {useHelperButtons && (
        <Flex
          justifyContent={'center'}
          alignItems="center"
          onClick={Increment}
          style={{
            height: '60px',
            width: '60px',
            flexShrink: 0,
            fontSize: '25px',
            backgroundColor: theme.palette.colors.petrol[400],
            color: 'white',
            cursor: 'pointer',
          }}
        >
          +
        </Flex>
      )}
    </Flex>
  );
};

function isValidateValue(
  value: string,
  { positive, decimalPrecision }: { positive?: boolean; decimalPrecision?: number },
): boolean {
  return (
    NUMBER_REGEX.test(value) &&
    (!positive || !value.startsWith('-')) &&
    (isNullish(decimalPrecision) || isDecimalValid(value, decimalPrecision))
  );
}

function isDecimalValid(value: string, decimalPrecision: number): boolean {
  const [, decimal] = value.split('.');
  return !decimal || decimal.length <= decimalPrecision;
}
